import { useEffect, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, EyeOff, Plus, Search } from "lucide-react";

const grupoKey = (g) => g.modelos.map((m) => m.id).sort().join(",");

function GrupoDuplicado({ grupo, indice, onFusionar, onIgnorar }) {
  const [sel, setSel] = useState(new Set(grupo.modelos.map((m) => m.id)));
  const [extra, setExtra] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);

  const miembros = [...grupo.modelos, ...extra];

  useEffect(() => {
    if (!q.trim()) { setResultados([]); return; }
    const t = setTimeout(() => {
      api.get("/modelos", { params: { q: q.trim() } })
        .then((r) => setResultados((r.data || []).filter((m) => !miembros.some((x) => x.id === m.id))))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const toggle = (id) => setSel((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const agregarModelo = (m) => {
    setExtra((prev) => [...prev, m]);
    setSel((prev) => new Set(prev).add(m.id));
    setQ(""); setResultados([]); setBuscando(false);
  };

  const idsSel = [...sel];

  return (
    <div data-testid={`duplicados-grupo-${indice}`} className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="text-xs font-semibold uppercase text-slate-500">{miembros.length} similares</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid={`duplicados-ignorar-${indice}`} onClick={onIgnorar}
            className="h-7 text-xs">
            <EyeOff size={12} className="mr-1" /> Ignorar grupo
          </Button>
          <Button size="sm" data-testid={`duplicados-fusionar-${indice}`}
            onClick={() => onFusionar(idsSel)} disabled={idsSel.length < 2}
            className="bg-indigo-600 hover:bg-indigo-700 text-white h-7 text-xs disabled:opacity-40">
            Fusionar seleccionados ({idsSel.length})
          </Button>
        </div>
      </div>
      <ul className="space-y-1.5">
        {miembros.map((m) => (
          <li key={m.id} data-testid={`duplicados-item-${indice}-${m.id}`} className="text-sm text-slate-700 flex items-center gap-2">
            <input type="checkbox" data-testid={`duplicados-check-${indice}-${m.id}`} checked={sel.has(m.id)}
              onChange={() => toggle(m.id)} className="accent-indigo-600" />
            <span className="font-medium">{m.nombre_mostrar}</span>
            {m.vehiculos != null && (
              <span className="text-xs text-slate-400">{m.vehiculos} vehículo{m.vehiculos === 1 ? "" : "s"}</span>
            )}
            {m.tiene_catalogo && (
              <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                Con catálogo
              </span>
            )}
            {m.marca_distinta && (
              <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200">
                Marca distinta
              </span>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-2">
        {!buscando && (
          <button type="button" data-testid={`duplicados-agregar-otro-${indice}`} onClick={() => setBuscando(true)}
            className="text-xs font-medium text-indigo-600 hover:underline inline-flex items-center gap-1">
            <Plus size={12} /> Agregar otro modelo…
          </button>
        )}
        {buscando && (
          <div className="relative max-w-xs">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input autoFocus data-testid={`duplicados-buscar-otro-${indice}`} value={q}
                onChange={(e) => setQ(e.target.value)} placeholder="Buscar modelo..." className="h-8 pl-8 text-sm" />
            </div>
            {resultados.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-48 overflow-y-auto">
                {resultados.map((m) => (
                  <button type="button" key={m.id} data-testid={`duplicados-res-${indice}-${m.id}`}
                    onClick={() => agregarModelo(m)}
                    className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50">
                    {m.nombre_mostrar}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function ModeloDuplicadosDialog({ abierto, onCerrar, onFusionarGrupo }) {
  const [cargando, setCargando] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [ignorados, setIgnorados] = useState(new Set());

  useEffect(() => {
    if (!abierto) return;
    setCargando(true);
    api.get("/modelos/duplicados")
      .then((r) => setGrupos(r.data || []))
      .catch((e) => toast.error(errorMsg(e)))
      .finally(() => setCargando(false));
  }, [abierto]);

  const visibles = grupos.filter((g) => !ignorados.has(grupoKey(g)));

  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) onCerrar?.(); }}>
      <DialogContent data-testid="modelo-duplicados-dialog" className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy size={18} /> Posibles modelos duplicados</DialogTitle>
          <DialogDescription>Grupos con nombres muy parecidos. Usted confirma cada fusión; nunca es automática.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3">
          {cargando && <p className="text-sm text-slate-400 py-6 text-center">Buscando…</p>}
          {!cargando && visibles.length === 0 && (
            <p data-testid="duplicados-vacio" className="text-sm text-slate-400 py-6 text-center">
              No se encontraron modelos con nombres parecidos.
            </p>
          )}
          {!cargando && visibles.map((g, i) => (
            <GrupoDuplicado key={grupoKey(g)} grupo={g} indice={i}
              onFusionar={(ids) => onFusionarGrupo(ids)}
              onIgnorar={() => setIgnorados((prev) => new Set(prev).add(grupoKey(g)))} />
          ))}
        </div>
        <div className="flex justify-end pt-3 border-t border-slate-100 shrink-0">
          <Button variant="outline" data-testid="duplicados-cerrar-btn"
            className="border-teal-500 text-teal-600 hover:bg-teal-50" onClick={() => onCerrar?.()}>Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
